import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringBuoy, MooringComponentType, MooringReviewDto, UpdateMooringBuoyDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { saveAs } from 'file-saver';
import { QueryFilter } from 'lib/tables/DataFilter';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { OfferViewContext } from '../../OfferViewContext';

@autoinject
export class BuoysContainer {
  @bindable
  protected mooringId: number;

  @bindable
  protected reviewId?: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  protected buoys: MooringBuoy[] = [];

  constructor(
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private t: I18N,
    private pubsub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService,
    private container: Container
  ) {
    pubsub.sub('filter-manager:filter-changed', (params: QueryFilter) => {
      void this.getBuoys(params);
    });
    pubsub.sub('entity:updated', () => {
      void this.getBuoys();
    });
    pubsub.sub('list-entity:updated', () => {
      void this.getBuoys();
    });
  }

  protected controlView: boolean;
  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  protected async exportBuoys() {
    const blob = await this.mooringService.exportProductionBuoys(this.mooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getBuoys();
  }

  @AsyncErrorHandler
  protected async getBuoys(queryFilter?: QueryFilter) {
    const { data } = await this.mooringService.getBuoys(this.mooringId, queryFilter);
    const lines = data;
    if (lines.length) {
      for (const line of lines) {
        line.Components?.sort((a, b) => a.SortIndex - b.SortIndex);
      }
      setTimeout(() => {
        this.buoys = lines;
      }, 10);
    }

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveBuoys({ entries, canNavigateOnSave }: { entries: MooringBuoy[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (buoy) =>
        ({
          Id: buoy.Id,
          Row: buoy.Row,
          Column: buoy.Column,
          MoorlogNo: buoy.MoorlogNo,
          AnalysisNo: buoy.AnalysisNo,
          DimForce: buoy.DimForce,
          IsAccident: buoy.IsAccident,
          Size: buoy.Size ? buoy.Size : 0,
          PullDownKg: buoy.PullDownKg ? buoy.PullDownKg : 0,
          PullDownMeter: buoy.PullDownMeter ? buoy.PullDownMeter : 0,
          ResidualDriftKg: buoy.ResidualDriftKg ? buoy.ResidualDriftKg : 0,
          ResidualPullDownMeter: buoy.ResidualPullDownMeter ? buoy.ResidualPullDownMeter : 0,
          IsLightBuoy: buoy.IsLightBuoy,
          MblAdditionalSafety: buoy.MblAdditionalSafety,
          MblRopes: buoy.MblRopes,
          MblSinkRopes: buoy.MblSinkRopes,
          MblMainStrap: buoy.MblMainStrap,
          MblStraps: buoy.MblStraps,
          SortIndex: buoy.SortIndex,
          //
          Components: buoy.Components,
        }) satisfies UpdateMooringBuoyDto
    );

    await this.mooringService.updateBuoys(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.buoy });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    } else {
      await this.getBuoys();
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.buoy_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.buoy_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'BUOYS', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
